import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');
  const invitationId = searchParams.get('invitationId');

  const acceptInvitation = async (
    accessToken: string
  ): Promise<{ tenantCode: string; role?: string; tenant?: any } | null> => {
    if (!invitationId) return null;

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const response = await fetch(`${baseUrl}/tenant-invitations/${invitationId}/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const accepted = await response.json();
        return {
          tenantCode: accepted.tenantCode,
          role: accepted.role,
          tenant: accepted.tenant,
        };
      } else {
        const errorText = await response.text();
        console.warn('Failed to accept invitation:', errorText);
        return null;
      }
    } catch (error) {
      console.warn('Error accepting invitation:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    if (!token) {
      setStatus('error');
      setMessage('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (password.length < 8) {
      setStatus('error');
      setMessage('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match.');
      return;
    }

    setStatus('loading');

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const response = await fetch(`${baseUrl}/auth/reset-password?token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Reset password failed');
      }

      const data = await response.json();

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));

      // If an invitation ID was provided, accept it now that we're authenticated
      const acceptedInvitation = await acceptInvitation(data.accessToken);
      if (acceptedInvitation) {
        const localUser = JSON.parse(localStorage.getItem('user') || '{}');
        const localTenants = Array.isArray(localUser.tenants) ? localUser.tenants : [];
        const hasAcceptedTenant = localTenants.some(
          (tenantMembership: any) => tenantMembership?.tenantCode === acceptedInvitation.tenantCode
        );

        if (!hasAcceptedTenant) {
          localTenants.push({
            tenantCode: acceptedInvitation.tenantCode,
            roles: acceptedInvitation.role ? [acceptedInvitation.role] : [],
            tenant: acceptedInvitation.tenant,
          });
          localStorage.setItem(
            'user',
            JSON.stringify({
              ...localUser,
              tenants: localTenants,
            })
          );
        }

        if (acceptedInvitation.tenant) {
          localStorage.setItem('currentTenant', JSON.stringify(acceptedInvitation.tenant));
        }
        localStorage.removeItem('isStandalone');
      }

      try {
        const userResponse = await fetch(`${baseUrl}/users/${data.user.id}`, {
          headers: {
            'Authorization': `Bearer ${data.accessToken}`,
          },
        });

        if (userResponse.ok) {
          const userWithTenants = await userResponse.json();
          localStorage.setItem('user', JSON.stringify(userWithTenants));

          const hasTenants = userWithTenants.tenants && userWithTenants.tenants.length > 0;
          if (!hasTenants && !acceptedInvitation) {
            localStorage.setItem('isStandalone', 'true');
            window.location.href = '/standalone-dashboard';
            return;
          }
          localStorage.removeItem('isStandalone');

          // If we just accepted an invitation, switch to that tenant
          if (acceptedInvitation) {
            const acceptedTenant = userWithTenants.tenants.find(
              (t: any) => t.tenantCode === acceptedInvitation.tenantCode
            );
            if (acceptedTenant?.tenant) {
              localStorage.setItem('currentTenant', JSON.stringify(acceptedTenant.tenant));
            }
          }
        }
      } catch (fetchError) {
        console.warn('Failed to fetch user details after password reset:', fetchError);
      }

      setStatus('success');
      setMessage('Your password has been reset. Redirecting...');
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Reset password failed. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        backgroundColor: '#3b4d66',
        paddingTop: '15vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '48px 40px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: 420,
          textAlign: 'center',
        }}
      >
        <img
          src="/logo-dv-full-text.png"
          alt="DV Logo"
          style={{
            height: '40px',
            marginBottom: '32px',
          }}
        />

        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
          Set a new password
        </Typography>

        {status === 'error' && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {message}
          </Alert>
        )}

        {status === 'success' && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {message}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
          }}
        >
          <TextField
            label="New password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
            disabled={status === 'loading' || status === 'success'}
            helperText="Minimum 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
            disabled={status === 'loading' || status === 'success'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    aria-label="toggle confirm password visibility"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={status === 'loading' || status === 'success'}
            sx={{ mt: 1 }}
          >
            {status === 'loading' ? 'Resetting...' : 'Reset password'}
          </Button>
        </Box>

        <Button
          variant="text"
          onClick={() => navigate(invitationId ? `/login?invitationId=${invitationId}` : '/login')}
          sx={{ mt: 2 }}
        >
          Back to login
        </Button>
      </Box>
    </Box>
  );
};
